import { useEffect, useMemo, useState } from "react";
import client from "../api/client";
import { getNextVoucherNo } from "../services/voucherNumberService";
import { useAuth } from "../context/AuthContext";
import { useFinancialYear } from "../context/FinancialYearContext";

const today = () => new Date().toISOString().split("T")[0];

const REFERENCE_TYPES = [
  {
    value: "ON_ACCOUNT",
    label: "On A/C",
    help: "General receipt without invoice adjustment",
  },
  {
    value: "TOWARDS_BILL",
    label: "Against Bill / Reference",
    help: "Customer payment against invoice/reference",
  },
  {
    value: "ADVANCE",
    label: "Advance",
    help: "Advance received before invoice",
  },
  {
    value: "NEW_BILL",
    label: "New Bill",
    help: "Receipt linked to a new manual bill reference",
  },
];

const getId = (x) => x?.Id ?? x?.id ?? x?.AccountId ?? x?.accountId ?? "";
const getAccountCode = (x) =>
  (x?.AccountCode ?? x?.accountCode ?? "").toString();
const getAccountName = (x) =>
  (x?.AccountName ?? x?.accountName ?? "").toString();
const getAccountType = (x) =>
  (x?.AccountType ?? x?.accountType ?? "").toString();
const getParentId = (x) =>
  (
    x?.ParentAccountId ??
    x?.parentAccountId ??
    x?.ParentId ??
    x?.parentId ??
    ""
  ).toString();
const getParentCode = (x) =>
  (
    x?.ParentAccountCode ??
    x?.parentAccountCode ??
    x?.ParentCode ??
    x?.parentCode ??
    ""
  ).toString();
const getParentName = (x) =>
  (
    x?.ParentAccountName ??
    x?.parentAccountName ??
    x?.ParentName ??
    x?.parentName ??
    ""
  ).toString();

const getCustomerId = (x) =>
  x?.Id ?? x?.id ?? x?.CustomerId ?? x?.customerId ?? "";
const getCustomerName = (x) =>
  x?.CustomerName ?? x?.customerName ?? x?.Name ?? x?.name ?? "";
const getCustomerCode = (x) =>
  x?.CustomerCode ?? x?.customerCode ?? x?.Code ?? x?.code ?? "";
const getCustomerAccountId = (x) =>
  x?.AccountId ??
  x?.accountId ??
  x?.LedgerAccountId ??
  x?.ledgerAccountId ??
  "";

const isParentControlLedger = (x) => {
  const code = getAccountCode(x);
  const name = getAccountName(x).toLowerCase();
  return (
    code === "1300" ||
    code === "2100" ||
    name.includes("accounts receivable") ||
    name.includes("accounts payable")
  );
};

const isGstLedger = (x) =>
  /gst|cgst|sgst|igst/i.test(`${getAccountCode(x)} ${getAccountName(x)}`);

const isCashBankLedger = (x) => {
  const name = getAccountName(x).toLowerCase();
  const code = getAccountCode(x);
  return (
    name.includes("cash") ||
    name.includes("bank") ||
    name.includes("upi") ||
    code === "1100" ||
    code === "1200"
  );
};

const isReceivableChildLedger = (x) => {
  if (isParentControlLedger(x)) return false;
  const code = getAccountCode(x);
  const name = getAccountName(x).toLowerCase();
  const parentId = getParentId(x);
  const parentCode = getParentCode(x);
  const parentName = getParentName(x).toLowerCase();

  return (
    (code.startsWith("130") && code !== "1300") ||
    parentId === "1300" ||
    parentCode === "1300" ||
    parentName.includes("accounts receivable") ||
    parentName.includes("sundry debtor") ||
    parentName.includes("debtor") ||
    name.includes("customer") ||
    name.includes("debtor")
  );
};

const isIncomeLedger = (x) => {
  const name = getAccountName(x).toLowerCase();
  const type = getAccountType(x).toLowerCase();
  return (
    type === "income" ||
    name.includes("income") ||
    name.includes("sales") ||
    name.includes("service revenue")
  );
};

const isAdvanceOrLiabilityLedger = (x) => {
  if (isParentControlLedger(x)) return false;
  const name = getAccountName(x).toLowerCase();
  const type = getAccountType(x).toLowerCase();
  return (
    type === "liability" ||
    type === "equity" ||
    name.includes("advance") ||
    name.includes("capital") ||
    name.includes("loan")
  );
};

const money = (v) =>
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const makeInitialForm = (date) => ({
  voucherNo: "",
  voucherDate: date || today(),
  receiptMode: "CASH",
  referenceType: "ON_ACCOUNT",
  billReferenceNo: "",
  customerId: "",
  debitAccountId: "",
  creditAccountId: "",
  amount: "",
  notes: "",
});

export default function ReceiptVoucherPage() {
  const { user } = useAuth();
  const { financialYear } = useFinancialYear();

  const companyId = Number(
    user?.companyId || localStorage.getItem("companyId") || 0,
  );
  const fyFrom = financialYear?.fromDate;
  const fyTo = financialYear?.toDate;
  const fyLocked = Boolean(financialYear?.isLocked || financialYear?.IsLocked);

  const [form, setForm] = useState(makeInitialForm(fyFrom));
  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [autoNarration, setAutoNarration] = useState(true);
  const [dateLocked, setDateLocked] = useState(false);
  const [checkingDateLock, setCheckingDateLock] = useState(false);

  const selectedDateOutsideFy =
    form.voucherDate && fyFrom && fyTo
      ? form.voucherDate < fyFrom || form.voucherDate > fyTo
      : false;

  const isPostingBlocked =
    fyLocked || dateLocked || selectedDateOutsideFy || checkingDateLock;

  useEffect(() => {
    if (fyFrom) {
      setForm((p) => ({ ...p, voucherDate: fyFrom, voucherNo: "" }));
    }
  }, [fyFrom]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    checkVoucherDateLock(form.voucherDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.voucherDate, companyId]);

  const checkVoucherDateLock = async (voucherDate) => {
    if (!voucherDate || !companyId) {
      setDateLocked(false);
      return;
    }

    try {
      setCheckingDateLock(true);
      const res = await client.get("/finance/is-date-locked", {
        params: { entryDate: voucherDate, companyId },
      });
      setDateLocked(Boolean(res.data?.isLocked));
    } catch (err) {
      console.error("Date lock check failed", err);
      setDateLocked(false);
    } finally {
      setCheckingDateLock(false);
    }
  };

  const findAccount = (mode, list = accounts) => {
    const m = (mode || "").toLowerCase();

    if (m === "cash") {
      return list.find(
        (x) =>
          getAccountName(x).toLowerCase().includes("cash") ||
          getAccountCode(x) === "1100",
      );
    }

    if (m === "bank") {
      return list.find(
        (x) =>
          getAccountName(x).toLowerCase().includes("bank") ||
          getAccountCode(x) === "1200",
      );
    }

    if (m === "upi") {
      return (
        list.find((x) => getAccountName(x).toLowerCase().includes("upi")) ||
        list.find((x) => getAccountName(x).toLowerCase().includes("bank"))
      );
    }

    return list.find((x) => getAccountType(x).toLowerCase() === "asset");
  };

  const loadData = async () => {
    try {
      const [accRes, custRes] = await Promise.all([
        client.get("/accounts"),
        client.get("/sales/customers"),
      ]);

      const acc = Array.isArray(accRes.data) ? accRes.data : [];
      const cust = Array.isArray(custRes.data) ? custRes.data : [];
      const cash = findAccount("CASH", acc);
      const nextNo = await getNextVoucherNo("receipt", fyFrom || today());

      setAccounts(acc);
      setCustomers(cust);

      setForm((prev) => ({
        ...prev,
        voucherDate: fyFrom || prev.voucherDate,
        voucherNo: prev.voucherNo || nextNo,
        debitAccountId: getId(cash) || "",
      }));
    } catch (err) {
      console.error(err);
      alert("Failed to load accounts/customers");
    }
  };

  const accountMap = useMemo(() => {
    const map = {};
    accounts.forEach((a) => {
      map[String(getId(a))] = `${getAccountCode(a)} - ${getAccountName(a)}`;
    });
    return map;
  }, [accounts]);

  const selectedCustomer = useMemo(
    () =>
      customers.find(
        (c) => String(getCustomerId(c)) === String(form.customerId),
      ),
    [customers, form.customerId],
  );

  const customerLedgerAccountId = useMemo(() => {
    const id = getCustomerAccountId(selectedCustomer);
    return id ? String(id) : "";
  }, [selectedCustomer]);

  const receiptDebitAccounts = useMemo(
    () => accounts.filter((a) => isCashBankLedger(a)),
    [accounts],
  );

  const receiptCreditAccounts = useMemo(
    () =>
      accounts.filter((a) => {
        if (isGstLedger(a) || isCashBankLedger(a) || isParentControlLedger(a))
          return false;

        if (form.referenceType === "TOWARDS_BILL")
          return isReceivableChildLedger(a);
        if (form.referenceType === "ADVANCE")
          return isReceivableChildLedger(a) || isAdvanceOrLiabilityLedger(a);
        if (form.referenceType === "NEW_BILL")
          return isReceivableChildLedger(a) || isIncomeLedger(a);

        return (
          isReceivableChildLedger(a) ||
          isIncomeLedger(a) ||
          isAdvanceOrLiabilityLedger(a)
        );
      }),
    [accounts, form.referenceType],
  );

  const recommendedCreditAccountId = useMemo(() => {
    if (customerLedgerAccountId) {
      const exists = accounts.some(
        (a) => String(getId(a)) === String(customerLedgerAccountId),
      );
      if (exists) return customerLedgerAccountId;
    }

    const customerName = getCustomerName(selectedCustomer).toLowerCase();

    const exactCustomer = customerName
      ? receiptCreditAccounts.find((a) =>
          getAccountName(a).toLowerCase().includes(customerName),
        )
      : null;

    const firstReceivableChild = receiptCreditAccounts.find((a) =>
      isReceivableChildLedger(a),
    );
    const income = receiptCreditAccounts.find((a) => isIncomeLedger(a));

    return (
      getId(
        exactCustomer ||
          firstReceivableChild ||
          income ||
          receiptCreditAccounts[0],
      ) || ""
    );
  }, [
    accounts,
    receiptCreditAccounts,
    selectedCustomer,
    customerLedgerAccountId,
  ]);

  const shouldLockCreditAccount =
    form.referenceType === "TOWARDS_BILL" &&
    !!form.customerId &&
    !!recommendedCreditAccountId;

  useEffect(() => {
    if (
      shouldLockCreditAccount ||
      !form.creditAccountId ||
      !receiptCreditAccounts.some(
        (a) => String(getId(a)) === String(form.creditAccountId),
      )
    ) {
      if (recommendedCreditAccountId) {
        setForm((prev) => ({
          ...prev,
          creditAccountId: recommendedCreditAccountId,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.referenceType,
    form.customerId,
    recommendedCreditAccountId,
    shouldLockCreditAccount,
  ]);

  const selectedCreditHint =
    form.referenceType === "TOWARDS_BILL"
      ? "Select exact Customer ledger only. Parent 1300 and GST ledgers are hidden."
      : form.referenceType === "ADVANCE"
        ? "Select Customer Advance / Liability or exact Customer ledger."
        : "Select Customer / Income / Liability ledger.";

  const debitName =
    accountMap[String(form.debitAccountId)] || "Cash / Bank A/c";
  const creditName =
    accountMap[String(form.creditAccountId)] || "Customer / Income A/c";

  const refreshVoucherNo = async () => {
    const no = await getNextVoucherNo("receipt", form.voucherDate);
    setForm((prev) => ({ ...prev, voucherNo: no }));
  };

  const buildNarration = (f = form) => {
    const refLabel =
      REFERENCE_TYPES.find((x) => x.value === f.referenceType)?.label ||
      "On A/C";
    const party = selectedCustomer
      ? getCustomerName(selectedCustomer)
      : "party";
    const ref = f.billReferenceNo?.trim()
      ? `, Ref: ${f.billReferenceNo.trim()}`
      : "";
    return `Being amount received from ${party} by ${f.receiptMode}${ref} (${refLabel})`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (isPostingBlocked && name !== "voucherDate") return;

    if (name === "voucherDate") {
      setForm((prev) => ({ ...prev, voucherDate: value, voucherNo: "" }));
      return;
    }

    if (name === "receiptMode") {
      const selectedAccount = findAccount(value);
      setForm((prev) => ({
        ...prev,
        receiptMode: value,
        debitAccountId: getId(selectedAccount) || "",
        notes: autoNarration ? "" : prev.notes,
      }));
      return;
    }

    if (name === "referenceType") {
      setForm((prev) => ({
        ...prev,
        referenceType: value,
        billReferenceNo:
          value === "ON_ACCOUNT" || value === "ADVANCE"
            ? ""
            : prev.billReferenceNo,
        creditAccountId: "",
        notes: autoNarration ? "" : prev.notes,
      }));
      return;
    }

    if (name === "customerId") {
      setForm((prev) => ({
        ...prev,
        customerId: value,
        creditAccountId: "",
        notes: autoNarration ? "" : prev.notes,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
      notes:
        autoNarration && name !== "notes"
          ? ""
          : name === "notes"
            ? value
            : prev.notes,
    }));
  };

  const clearForm = async () => {
    const defaultAccount = findAccount(form.receiptMode);
    const nextNo = await getNextVoucherNo("receipt", fyFrom || today());

    setForm({
      ...makeInitialForm(fyFrom || today()),
      voucherNo: nextNo,
      receiptMode: form.receiptMode,
      debitAccountId: getId(defaultAccount) || "",
    });
  };

  const validationErrors = useMemo(() => {
    const errors = [];

    if (fyLocked)
      errors.push(
        "Selected financial year is locked. Receipt cannot be posted.",
      );
    if (dateLocked)
      errors.push("Selected voucher date belongs to a locked financial year.");
    if (selectedDateOutsideFy)
      errors.push(`Voucher date must be between ${fyFrom} and ${fyTo}.`);
    if (!companyId) errors.push("Company is required. Please login again.");
    if (!form.voucherNo.trim()) errors.push("Voucher No is required");
    if (!form.debitAccountId) errors.push("Select Debit Account");
    if (!form.creditAccountId) errors.push("Select Credit Account");

    if (
      String(form.debitAccountId) === String(form.creditAccountId) &&
      form.debitAccountId
    ) {
      errors.push("Debit and Credit account cannot be same");
    }

    const creditAccount = accounts.find(
      (a) => String(getId(a)) === String(form.creditAccountId),
    );

    if (creditAccount && isParentControlLedger(creditAccount)) {
      errors.push(
        "Do not post directly to parent control account. Select exact customer ledger.",
      );
    }

    if (
      form.referenceType === "TOWARDS_BILL" &&
      form.customerId &&
      !form.creditAccountId
    ) {
      errors.push("Customer ledger account is required");
    }

    if (
      (form.referenceType === "NEW_BILL" ||
        form.referenceType === "TOWARDS_BILL") &&
      !form.billReferenceNo.trim()
    ) {
      errors.push("Bill / Reference No is required");
    }

    if (!form.amount || Number(form.amount) <= 0) {
      errors.push("Enter valid amount");
    }

    return errors;
  }, [
    form,
    accounts,
    fyLocked,
    dateLocked,
    selectedDateOutsideFy,
    companyId,
    fyFrom,
    fyTo,
  ]);

  const save = async () => {
    if (checkingDateLock)
      return alert("Please wait. Checking financial year lock status.");
    if (validationErrors.length) return alert(validationErrors[0]);

    try {
      setSaving(true);

      await client.post("/vouchers/receipt", {
        companyId,
        voucherNo: form.voucherNo.trim(),
        voucherDate: form.voucherDate,
        receiptMode: form.receiptMode,
        referenceType: form.referenceType,
        billReferenceNo: form.billReferenceNo.trim() || null,
        customerId: form.customerId ? Number(form.customerId) : null,
        debitAccountId: Number(form.debitAccountId),
        creditAccountId: Number(form.creditAccountId),
        amount: Number(form.amount),
        notes: (form.notes || buildNarration()).trim(),
      });

      alert("Receipt saved and journal posted");
      await clearForm();
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          err?.response?.data?.title ||
          err?.response?.data ||
          err?.message ||
          "Failed to save receipt",
      );
    } finally {
      setSaving(false);
    }
  };

  const effectiveNotes = form.notes || buildNarration();

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "600",
    marginBottom: "3px",
  };
  const inputStyle = { height: "30px", fontSize: "12px", borderRadius: "6px" };
  const buttonStyle = {
    border: "none",
    padding: "5px 14px",
    fontSize: "12px",
    borderRadius: "6px",
  };

  return (
    <div style={{ padding: "0px", fontSize: "13px" }}>
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "linear-gradient(90deg,#0f766e,#20c997)",
            color: "#fff",
            padding: "10px 14px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: "15px", fontWeight: "700" }}>
              Receipt Voucher
            </div>
            <div style={{ fontSize: "11px", opacity: 0.9 }}>
              Cash/Bank Dr → Customer/Income/Liability Cr
            </div>
          </div>

          <span
            style={{
              background: isPostingBlocked ? "#dc3545" : "#fff",
              color: isPostingBlocked ? "#fff" : "#0f766e",
              padding: "3px 8px",
              borderRadius: "6px",
              fontSize: "11px",
              fontWeight: "600",
            }}
          >
            {checkingDateLock
              ? "Checking FY"
              : isPostingBlocked
                ? "Posting Blocked"
                : "Auto Journal"}
          </span>
        </div>

        <div style={{ padding: "12px" }}>
          {checkingDateLock && (
            <div className="alert alert-info py-2" style={{ fontSize: 11 }}>
              Checking financial year lock status...
            </div>
          )}
          {fyLocked && (
            <div className="alert alert-danger py-2" style={{ fontSize: 11 }}>
              Selected financial year is locked. Receipt posting is blocked.
            </div>
          )}
          {dateLocked && (
            <div className="alert alert-danger py-2" style={{ fontSize: 11 }}>
              Selected voucher date belongs to a locked financial year.
            </div>
          )}
          {selectedDateOutsideFy && (
            <div className="alert alert-warning py-2" style={{ fontSize: 11 }}>
              Voucher date must be between {fyFrom} and {fyTo}.
            </div>
          )}

          <div className="row g-2">
            <div className="col-md-3">
              <label style={labelStyle}>Voucher No</label>
              <input
                name="voucherNo"
                className="form-control"
                style={inputStyle}
                value={form.voucherNo}
                readOnly
              />
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mt-1"
                style={{ fontSize: "11px", padding: "2px 8px" }}
                disabled={isPostingBlocked}
                onClick={refreshVoucherNo}
              >
                Refresh No
              </button>
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>Date</label>
              <input
                type="date"
                name="voucherDate"
                className="form-control"
                style={inputStyle}
                min={fyFrom || undefined}
                max={fyTo || undefined}
                value={form.voucherDate}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>Receipt Mode</label>
              <select
                name="receiptMode"
                className="form-select"
                style={inputStyle}
                value={form.receiptMode}
                disabled={isPostingBlocked}
                onChange={handleChange}
              >
                <option value="CASH">Cash</option>
                <option value="BANK">Bank</option>
                <option value="UPI">UPI</option>
              </select>
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>Reference Type</label>
              <select
                name="referenceType"
                className="form-select"
                style={inputStyle}
                value={form.referenceType}
                disabled={isPostingBlocked}
                onChange={handleChange}
              >
                {REFERENCE_TYPES.map((x) => (
                  <option key={x.value} value={x.value}>
                    {x.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>Bill / Reference No</label>
              <input
                name="billReferenceNo"
                className="form-control"
                style={inputStyle}
                value={form.billReferenceNo}
                onChange={handleChange}
                disabled={
                  isPostingBlocked ||
                  form.referenceType === "ON_ACCOUNT" ||
                  form.referenceType === "ADVANCE"
                }
                placeholder="Invoice / bill no"
              />
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>Customer</label>
              <select
                name="customerId"
                className="form-select"
                style={inputStyle}
                value={form.customerId}
                disabled={isPostingBlocked}
                onChange={handleChange}
              >
                <option value="">Optional - Select Customer</option>
                {customers.map((c) => (
                  <option key={getCustomerId(c)} value={getCustomerId(c)}>
                    {getCustomerCode(c)} - {getCustomerName(c)}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>Debit Account - Cash/Bank/Asset</label>
              <select
                name="debitAccountId"
                className="form-select"
                style={inputStyle}
                value={form.debitAccountId}
                disabled={isPostingBlocked}
                onChange={handleChange}
              >
                <option value="">Select Cash / Bank / UPI</option>
                {(receiptDebitAccounts.length
                  ? receiptDebitAccounts
                  : accounts
                ).map((a) => (
                  <option key={getId(a)} value={getId(a)}>
                    {getAccountCode(a)} - {getAccountName(a)}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>
                Credit Account - Customer/Income/Cr
              </label>
              <select
                name="creditAccountId"
                className="form-select"
                style={inputStyle}
                value={form.creditAccountId}
                disabled={isPostingBlocked || shouldLockCreditAccount}
                onChange={handleChange}
              >
                <option value="">Select Customer / Income / Liability</option>
                {receiptCreditAccounts.map((a) => (
                  <option key={getId(a)} value={getId(a)}>
                    {getAccountCode(a)} - {getAccountName(a)}
                  </option>
                ))}
              </select>
              <div
                style={{ fontSize: "10px", color: "#0f766e", marginTop: "3px" }}
              >
                {selectedCreditHint}
              </div>
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>Amount</label>
              <input
                type="number"
                name="amount"
                className="form-control"
                style={{ ...inputStyle, textAlign: "right" }}
                value={form.amount}
                disabled={isPostingBlocked}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-9">
              <label style={labelStyle}>Notes</label>
              <input
                name="notes"
                className="form-control"
                style={inputStyle}
                value={effectiveNotes}
                disabled={isPostingBlocked}
                onChange={(e) => {
                  setAutoNarration(false);
                  setForm((p) => ({ ...p, notes: e.target.value }));
                }}
              />
            </div>
          </div>

          <div
            style={{
              marginTop: 8,
              display: "flex",
              gap: 14,
              alignItems: "center",
              fontSize: 11,
            }}
          >
            <label style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <input
                type="checkbox"
                checked={autoNarration}
                disabled={isPostingBlocked}
                onChange={(e) => {
                  setAutoNarration(e.target.checked);
                  if (e.target.checked) setForm((p) => ({ ...p, notes: "" }));
                }}
              />
              Auto narration
            </label>
            <span style={{ color: "#0f766e" }}>
              {
                REFERENCE_TYPES.find((x) => x.value === form.referenceType)
                  ?.help
              }
            </span>
          </div>

          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              border: "1px dashed #b7d7c2",
              background: "#f8fffb",
              borderRadius: "8px",
              fontSize: "12px",
            }}
          >
            <div style={{ fontWeight: "700", marginBottom: "5px" }}>
              Journal Preview
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>{debitName} Dr</span>
              <b>₹ {money(form.amount)}</b>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>To {creditName}</span>
              <b>₹ {money(form.amount)}</b>
            </div>
            <div style={{ marginTop: 5, color: "#0f766e", fontSize: 11 }}>
              {effectiveNotes}
            </div>
          </div>

          {validationErrors.length > 0 && (
            <div style={{ marginTop: 8, color: "#dc3545", fontSize: 11 }}>
              {validationErrors[0]}
            </div>
          )}

          <div style={{ marginTop: "12px", display: "flex", gap: "8px" }}>
            <button
              onClick={save}
              disabled={
                saving || validationErrors.length > 0 || isPostingBlocked
              }
              style={{
                ...buttonStyle,
                background:
                  saving || validationErrors.length > 0 || isPostingBlocked
                    ? "#8abfa4"
                    : "#0f766e",
                color: "#fff",
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>

            <button
              onClick={clearForm}
              type="button"
              disabled={saving}
              style={{ ...buttonStyle, background: "#6c757d", color: "#fff" }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
